import { logger } from '../../../utils/dccLogger'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import initParametersSelector from '../../../redux/selectors/initParametersSelector'
import { isGrpLoggedInSelector } from '../../../redux/selectors/e3DGrpAuthSelectors'
import { isAuthenticated } from '../../../services/e3DAuthService'

const E3D_SID_STORAGE_KEY = 'e3d_sid'
const E3D_RESUME_PENDING_KEY = 'e3d_resume_pending'

const useE3DParamsInit = () => {
    const { entryUrl } = useSelector(initParametersSelector)
    const isGrpAuthenticated = useSelector(isGrpLoggedInSelector)
    const [wasE3DSidUsed, setWasE3DSidUsed] = useState(false)
    const hasAttemptedResumeRef = useRef<string | null>(null)

    const removeE3DSidFromUrl = () => {
        try {
            const currentUrl = new URL(window.location.href)
            currentUrl.searchParams.delete('e3d-sid')
            const newUrl =
                currentUrl.pathname +
                (currentUrl.searchParams.toString() ? `?${currentUrl.searchParams.toString()}` : '')
            logger.info(`Removing e3d-sid parameter from URL: ${newUrl}`)
            window.history.replaceState({}, document.title, newUrl)
        } catch (error) {
            logger.error(`Error removing e3d-sid parameter: ${error}`)
        }
    }

    useEffect(() => {
        let mounted = true

        const handleE3DStreamEnd = () => {
            if (mounted) {
                logger.info('E3D stream end event received, removing e3d-sid parameter')
                removeE3DSidFromUrl()
                try {
                    sessionStorage.removeItem(E3D_SID_STORAGE_KEY)
                    sessionStorage.removeItem(E3D_RESUME_PENDING_KEY)
                } catch { /* no-op */ }
                setWasE3DSidUsed(false)
            }
        }

        const initializeE3D = async () => {
            try {
                let e3dSid: string | null = null

                try {
                    const currentUrlParams = new URLSearchParams(window.location.search)
                    e3dSid = currentUrlParams.get('e3d-sid')
                } catch (error) {
                    logger.warn(`Error reading current URL params: ${error}`)
                }

                if (!e3dSid && entryUrl) {
                    try {
                        const entryUrlObj = new URL(entryUrl)
                        e3dSid = entryUrlObj.searchParams.get('e3d-sid')
                    } catch (error) {
                        logger.warn(`Error reading entryUrl params: ${error}`)
                    }
                }

                if (!e3dSid) {
                    try {
                        e3dSid = sessionStorage.getItem(E3D_SID_STORAGE_KEY)
                    } catch { /* no-op */ }
                }

                if (!e3dSid) {
                    try { sessionStorage.removeItem(E3D_RESUME_PENDING_KEY) } catch { /* no-op */ }
                    return
                }

                try {
                    sessionStorage.setItem(E3D_SID_STORAGE_KEY, e3dSid)
                    sessionStorage.setItem(E3D_RESUME_PENDING_KEY, '1')
                } catch { /* no-op */ }

                if (hasAttemptedResumeRef.current === e3dSid) {
                    return
                }

                const currentUrl = new URL(window.location.href)
                currentUrl.searchParams.set('e3d-sid', e3dSid)
                const newUrl =
                    currentUrl.pathname +
                    (currentUrl.searchParams.toString() ? `?${currentUrl.searchParams.toString()}` : '')

                logger.info(`Updating URL with e3d-sid parameter: ${newUrl}`)
                window.history.replaceState({}, document.title, newUrl)

                if (window.E3D && isGrpAuthenticated && isAuthenticated()) {
                    if (window.E3D.resumeSessionFromParams) {
                        try {
                            try {
                                const sessionData = await window.E3D.getSessionData(e3dSid)
                                if (!mounted) {
                                    return
                                }

                                if (sessionData?.streamActive) {
                                    logger.info('Active E3D stream found, resuming session')
                                    window.E3D.resumeSessionFromParams()
                                    hasAttemptedResumeRef.current = e3dSid
                                    setWasE3DSidUsed(true)
                                    try { sessionStorage.removeItem(E3D_RESUME_PENDING_KEY) } catch { /* no-op */ }
                                    return
                                }
                            } catch (error) {
                                logger.warn(`Could not check session data, attempting resume anyway: ${error}`)
                            }

                            logger.info('Attempting to resume E3D session from params')
                            window.E3D.resumeSessionFromParams()
                            hasAttemptedResumeRef.current = e3dSid
                            setWasE3DSidUsed(true)
                            try { sessionStorage.removeItem(E3D_RESUME_PENDING_KEY) } catch { /* no-op */ }
                        } catch (error) {
                            if (mounted) {
                                logger.error(`Error resuming E3D session: ${error}`)
                            }
                        }
                    } else {
                        logger.info('E3D not fully initialized yet, will retry when initialized')
                    }
                } else {
                    logger.info('E3D not available or user not authenticated yet, will retry when authenticated')
                }
            } catch (error) {
                if (mounted) {
                    logger.error(`Error handling E3D session parameters: ${error}`)
                }
            }
        }

        initializeE3D()
        window.addEventListener('e3d-stream-end', handleE3DStreamEnd)

        return () => {
            mounted = false
            window.removeEventListener('e3d-stream-end', handleE3DStreamEnd)
        }
    }, [entryUrl, isGrpAuthenticated])

    return { wasE3DSidUsed }
}

export default useE3DParamsInit
